import React from 'react';

/**
 * Icon set for the Contacts workspace.
 *
 * Matches the house style used by MarketingIcons and HomeHubParts: a 24-unit
 * viewBox, 1.6 stroke, round caps, and `currentColor` so each icon inherits
 * the state colour of the control it sits in.
 */
const Icon = ({ size = 18, children, label, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={label ? undefined : 'true'}
    role={label ? 'img' : undefined}
    focusable="false"
    {...rest}
  >
    {label ? <title>{label}</title> : null}
    {children}
  </svg>
);

export const SearchIcon = (props) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </Icon>
);

export const FilterIcon = (props) => (
  <Icon {...props}>
    <path d="M4 6h16" />
    <path d="M7 12h10" />
    <path d="M10 18h4" />
  </Icon>
);

export const CloseIcon = (props) => (
  <Icon {...props}>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </Icon>
);

export const ChevronDownIcon = (props) => (
  <Icon {...props} size={props.size || 14}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);

export const RowsIcon = (props) => (
  <Icon {...props}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </Icon>
);

export const GridIcon = (props) => (
  <Icon {...props}>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </Icon>
);

export const FlagIcon = ({ filled, ...props }) => (
  <Icon {...props}>
    <path d="M5 21V4.5" />
    <path
      d="M5 4.5h11.2l-1.7 3.6 1.7 3.6H5z"
      fill={filled ? 'currentColor' : 'none'}
      fillOpacity={filled ? 0.9 : 0}
    />
  </Icon>
);

export const MoreIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="5.5" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);

export const MailIcon = (props) => (
  <Icon {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 7.2l8.5 6 8.5-6" />
  </Icon>
);

export const PhoneIcon = (props) => (
  <Icon {...props}>
    <path d="M21 16.9v2.6a1.9 1.9 0 01-2.1 1.9 18.8 18.8 0 01-8.2-2.9 18.5 18.5 0 01-5.7-5.7A18.8 18.8 0 012.1 4.6 1.9 1.9 0 014 2.5h2.6a1.9 1.9 0 011.9 1.6 12 12 0 00.7 2.7 1.9 1.9 0 01-.4 2L7.6 10a15 15 0 005.7 5.7l1.2-1.2a1.9 1.9 0 012-.4 12 12 0 002.7.7A1.9 1.9 0 0121 16.9z" />
  </Icon>
);

export const LinkedInIcon = (props) => (
  <Icon {...props}>
    <path d="M16 8.5a5.5 5.5 0 015.5 5.5V20h-3.6v-6a1.9 1.9 0 00-3.8 0v6h-3.6v-11h3.6v1.4A5.4 5.4 0 0116 8.5z" />
    <rect x="2.6" y="9" width="3.6" height="11" rx="0.6" />
    <circle cx="4.4" cy="4.6" r="1.9" />
  </Icon>
);

export const GlobeIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a15 15 0 010 18 15 15 0 010-18z" />
  </Icon>
);

export const DownloadIcon = (props) => (
  <Icon {...props}>
    <path d="M12 4v10" />
    <path d="M8.2 10.4L12 14.2l3.8-3.8" />
    <path d="M4.5 18.5h15" />
  </Icon>
);

export const ShareIcon = (props) => (
  <Icon {...props}>
    <circle cx="17.5" cy="5.5" r="2.5" />
    <circle cx="6.5" cy="12" r="2.5" />
    <circle cx="17.5" cy="18.5" r="2.5" />
    <path d="M8.8 10.8l6.4-3.8" />
    <path d="M8.8 13.2l6.4 3.8" />
  </Icon>
);

export const SendIcon = (props) => (
  <Icon {...props}>
    <path d="M21 3L10.5 13.5" />
    <path d="M21 3l-6.6 18-3.9-8.5L2 8.6 21 3z" />
  </Icon>
);

export const CalendarIcon = (props) => (
  <Icon {...props}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M8 3v3.5" />
    <path d="M16 3v3.5" />
    <path d="M3.5 10h17" />
  </Icon>
);

export const PinIcon = (props) => (
  <Icon {...props}>
    <path d="M12 21s7-5.7 7-11a7 7 0 10-14 0c0 5.3 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.6" />
  </Icon>
);

export const PencilIcon = (props) => (
  <Icon {...props}>
    <path d="M4 20h4l10-10-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </Icon>
);

export const TrashIcon = (props) => (
  <Icon {...props}>
    <path d="M4.5 7h15" />
    <path d="M9.5 7V4.5h5V7" />
    <path d="M6.5 7l.8 12.2a1.5 1.5 0 001.5 1.3h6.4a1.5 1.5 0 001.5-1.3L17.5 7" />
    <path d="M10.5 11v6" />
    <path d="M13.5 11v6" />
  </Icon>
);

export const PlusIcon = (props) => (
  <Icon {...props}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Icon>
);

export const UsersIcon = (props) => (
  <Icon {...props}>
    <path d="M16.5 20v-1.5a4 4 0 00-4-4h-6a4 4 0 00-4 4V20" />
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M21.5 20v-1.5a4 4 0 00-2.8-3.8" />
    <path d="M16 4.6a3.5 3.5 0 010 6.8" />
  </Icon>
);

export const NoteIcon = (props) => (
  <Icon {...props}>
    <path d="M5 4.5h14v10.8L14.8 19.5H5z" />
    <path d="M19 15.2h-4.2v4.3" />
    <path d="M8.5 9h7" />
    <path d="M8.5 12.5h4" />
  </Icon>
);

export const CheckIcon = (props) => (
  <Icon {...props}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Icon>
);

export const CopyIcon = (props) => (
  <Icon {...props}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M15 6.5V5.5a2 2 0 00-2-2H5.5a2 2 0 00-2 2V13a2 2 0 002 2h1" />
  </Icon>
);

export const UndoIcon = (props) => (
  <Icon {...props}>
    <path d="M4 9h9.5a5 5 0 010 10H9" />
    <path d="M7.5 5.5L4 9l3.5 3.5" />
  </Icon>
);

export const AlertIcon = (props) => (
  <Icon {...props}>
    <path d="M12 4l9 15.5H3L12 4z" />
    <path d="M12 10v4" />
    <path d="M12 17h.01" />
  </Icon>
);

export const CardIcon = (props) => (
  <Icon {...props}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <path d="M7 11h5" />
    <path d="M7 14.5h9" />
  </Icon>
);

export const ArrowRightIcon = (props) => (
  <Icon {...props}>
    <path d="M5 12h14" />
    <path d="M13.5 6.5L20 12l-6.5 5.5" />
  </Icon>
);

export const SparkIcon = (props) => (
  <Icon {...props}>
    <path d="M12 3.5l1.9 5.1 5.1 1.9-5.1 1.9L12 17.5l-1.9-5.1L5 10.5l5.1-1.9L12 3.5z" />
  </Icon>
);
